import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui.tsx';

/** A disclosure of real buttons/links, portaled outside scrolling tables.
 * Keep children mounted when closed: a confirmation or an in-flight action
 * must survive an outside click. Native Tab order and Escape work for every
 * nested control, including the existing Send split button.
 */
export function QuoteActionsOverflow({ label, children }: { label: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const id = useId();
  const anchor = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const restoreFocus = () => anchor.current?.querySelector('button')?.focus();

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = anchor.current?.getBoundingClientRect();
      const box = panel.current?.getBoundingClientRect();
      if (!rect || !box) return;
      setPosition({
        left: Math.max(8, Math.min(rect.right - box.width, window.innerWidth - box.width - 8)),
        top: Math.max(8, Math.min(rect.bottom + 6, window.innerHeight - box.height - 8)),
      });
    };
    place();
    panel.current?.querySelector<HTMLElement>('button:not(:disabled), a[href], input')?.focus();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      // Dialogs and nested split menus have their own portals and focus rules.
      if (target instanceof Element && target.closest('[role="dialog"], [role="menu"]')) return;
      if (!anchor.current?.contains(target) && !panel.current?.contains(target)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);

  return (
    <>
      <div ref={anchor} className="inline-block">
        <Button variant="ghost" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
          {label}
        </Button>
      </div>
      {createPortal(
        <div
          ref={panel}
          id={id}
          hidden={!open}
          role="group"
          aria-label={label}
          className="fixed z-40 max-h-[70vh] w-max max-w-[calc(100vw-1rem)] overflow-auto rounded-lg border border-[color:var(--color-line)] bg-white p-3 shadow-lg"
          style={{ ...position, maxWidth: 'calc(100vw - 16px)' }}
          onKeyDown={(event) => {
            if (event.key === 'Escape' && !(event.target as Element).closest('[role="dialog"], [role="menu"]')) {
              event.preventDefault();
              event.stopPropagation();
              setOpen(false);
              restoreFocus();
            }
          }}
        >
          <div className="flex max-w-xs flex-col items-stretch gap-2 text-left">{children}</div>
        </div>,
        document.body,
      )}
    </>
  );
}
